/**
 * D1's fork, as a pure machine.
 *
 * 🔴 D1 IS NOT A MENU, IT IS A FORK THAT PROTECTS A 30-MINUTE SLOT.
 * `wireframes.md` D1: *"two jobs, two times, two doors"*. A teacher at 10:05
 * with twenty children in front of her is doing exactly one of these, and a
 * screen that offers eight options costs her the first two minutes of the slot.
 *
 * Pure and injected-free for the reason `kidFlow.ts` is: navigation that lives
 * in component state cannot be tested, and the transition that matters here —
 * *you cannot reach a game without passing the confirm screen* — is a claim
 * about the graph, not about a button.
 */

export type TeacherScreen =
  | { name: "D1" }
  | { name: "D2"; classId: string }
  | { name: "D3"; classId: string; playerId: string }
  | { name: "D4"; classId: string }
  | { name: "D5"; classId: string; playerId: string }
  | { name: "D6"; classId: string }
  | { name: "D7"; classId: string }
  /**
   * Every child, across every class — the reading surface, reached from the
   * sidebar rather than from the fork.
   *
   * 🔴 IT CARRIES NO `classId`, AND THAT IS THE POINT. D2 and D4–D7 are all
   * *"this class"*; this one is *"all of them"*, so binding it to the open class
   * would be a contradiction the type system should refuse rather than a rule
   * somebody has to remember.
   */
  | { name: "STUDENTS" }
  /**
   * T7 — today's register for the open class.
   *
   * 🔴 IT CARRIES THE `classId`, UNLIKE `STUDENTS`. A register is a class and a
   * day; "attendance across every class I teach" is not a thing a teacher takes,
   * and a screen that offered it would be one where the wrong section gets
   * marked.
   */
  | { name: "ATTENDANCE"; classId: string }
  /**
   * D9 — the conversation with a family, and the notices sent to the class.
   */
  | { name: "MESSAGES"; classId: string }
  /**
   * How this class signs in on a tablet — the class code, and the register the
   * children type against.
   */
  | { name: "PLAY"; classId: string };

export type TeacherAction =
  | { type: "CHOOSE_PLAY" }
  | { type: "OPEN_PLAY_SETUP" }
  | { type: "CHOOSE_DASHBOARD" }
  | { type: "PICK_CHILD"; playerId: string }
  | { type: "OPEN_CHILD"; playerId: string; classId?: string }
  | { type: "OPEN_LEADERBOARD" }
  | { type: "OPEN_REPORTS" }
  | { type: "OPEN_STUDENTS" }
  | { type: "OPEN_ATTENDANCE" }
  | { type: "OPEN_MESSAGES" }
  | { type: "LAUNCH" }
  | { type: "BACK" };

export interface TeacherFlowState {
  screen: TeacherScreen;
  classId: string;
  playingAs: string | null;
}

export const initialTeacherFlow = (classId: string): TeacherFlowState => ({
  screen: { name: "D4", classId },
  classId,
  playingAs: null,
});

export function teacherFlow(
  state: TeacherFlowState,
  action: TeacherAction,
): TeacherFlowState {
  const { classId, screen } = state;

  switch (action.type) {
    case "CHOOSE_PLAY":
      return { ...state, screen: { name: "D2", classId }, playingAs: null };

    case "OPEN_PLAY_SETUP":
      return { ...state, screen: { name: "D2", classId }, playingAs: null };

    case "CHOOSE_DASHBOARD":
      return { ...state, screen: { name: "D4", classId } };

    case "PICK_CHILD":
      return {
        ...state,
        screen: { name: "D3", classId, playerId: action.playerId },
      };

    case "OPEN_CHILD":
      return {
        ...state,
        screen: {
          name: "D5",
          classId: action.classId ?? classId,
          playerId: action.playerId,
        },
      };

    case "OPEN_STUDENTS":
      return { ...state, screen: { name: "STUDENTS" } };

    case "OPEN_ATTENDANCE":
      return { ...state, screen: { name: "ATTENDANCE", classId } };

    case "OPEN_MESSAGES":
      return { ...state, screen: { name: "MESSAGES", classId } };

    case "OPEN_LEADERBOARD":
      return { ...state, screen: { name: "D6", classId } };

    case "OPEN_REPORTS":
      return { ...state, screen: { name: "D7", classId } };

    case "LAUNCH":
      if (screen.name !== "D3") return state;
      return { ...state, playingAs: screen.playerId };

    case "BACK":
      switch (screen.name) {
        case "D1":
        case "D4":
          return state;
        case "D3":
          return { ...state, screen: { name: "D2", classId }, playingAs: null };
        case "STUDENTS":
        case "ATTENDANCE":
        case "MESSAGES":
        case "PLAY":
        case "D2":
        case "D5":
        case "D6":
        case "D7":
          return { ...state, screen: { name: "D4", classId } };
        default:
          return { ...state, screen: { name: "D4", classId }, playingAs: null };
      }

    default:
      return state;
  }
}

/**
 * The line D3 shows, and the reason the screen exists.
 *
 * 🔴 IT NAMES THE CHILD **AND** THE ROLL NUMBER. `wireframes.md` D3:
 * *"Playing as Kabir — progress saves to roll 03."* The name alone is not
 * enough — two children called Aarav is the ordinary case in an Indian
 * classroom, and the roll number is the thing the record is actually keyed on.
 */
export function launchConfirmLine(child: {
  name: string;
  rollNumber: string | null;
}): string {
  const roll = child.rollNumber;
  return roll
    ? `Playing as ${child.name} — progress saves to roll ${roll}.`
    : `Playing as ${child.name}.`;
}
